import type { LlmBlockCounts } from "@/services/api";
import { llmReviewMessages } from "@/services/messages";
import { AlertTriangle, Check, CircleSlash, Info, Loader2, XCircle } from "lucide-react";

interface SummaryCardsProps {
	counts: LlmBlockCounts;
}

interface CardSpec {
	label: string;
	hint: string;
	count: number;
	icon: typeof Check;
	colorClass: string;
	hidden?: boolean;
	spinning?: boolean;
}

/**
 * Сводка-карточки US1: 5 счётчиков состояний LLM-ревью.
 * Цветовая семантика:
 *   - Applied (green)              — LLM подтвердил/уточнил.
 *   - SkippedWithConcern (amber)   — реально-проблемные пропуски.
 *   - SkippedRoutine (slate)       — штатные пропуски (no_coded_line).
 *   - Failed (red)                 — сетевая ошибка / некорректный ответ.
 *   - Pending (blue + spinner)     — в работе (показывается только при > 0).
 *
 * "None"-карточка опционально (если все блоки `none` — секция вообще не
 * показывается родительским `LlmReviewSection`, см. логику empty_state там).
 */
export function SummaryCards({ counts }: SummaryCardsProps): React.JSX.Element {
	const m = llmReviewMessages;
	const skippedRoutine = counts.skipped - counts.skippedWithConcern;

	const cards: CardSpec[] = [
		{
			label: m.counter_applied,
			hint: m.counter_applied_hint,
			count: counts.applied,
			icon: Check,
			colorClass: "border-emerald-200 bg-emerald-50 text-emerald-900",
		},
		{
			label: m.counter_skipped_concern,
			hint: m.counter_skipped_concern_hint,
			count: counts.skippedWithConcern,
			icon: AlertTriangle,
			colorClass: "border-amber-200 bg-amber-50 text-amber-900",
		},
		{
			label: m.counter_skipped_routine,
			hint: m.counter_skipped_routine_hint,
			count: skippedRoutine,
			icon: CircleSlash,
			colorClass: "border-slate-200 bg-slate-50 text-slate-700",
		},
		{
			label: m.counter_failed,
			hint: m.counter_failed_hint,
			count: counts.failed,
			icon: XCircle,
			colorClass: "border-red-200 bg-red-50 text-red-900",
		},
		{
			label: m.counter_pending,
			hint: m.counter_pending_hint,
			count: counts.pending,
			icon: Loader2,
			colorClass: "border-blue-200 bg-blue-50 text-blue-900",
			hidden: counts.pending === 0,
			spinning: true,
		},
		{
			label: m.counter_none,
			hint: m.counter_none_hint,
			count: counts.none,
			icon: Info,
			colorClass: "border-slate-100 bg-white text-slate-600",
			hidden: counts.none === 0,
		},
	];

	return (
		<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
			{cards
				.filter((c) => !c.hidden)
				.map((c) => {
					const Icon = c.icon;
					return (
						<div
							key={c.label}
							className={`rounded-lg border px-3 py-2.5 ${c.colorClass}`}
							title={c.hint}
						>
							<div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide opacity-80">
								<Icon
									className={`h-3.5 w-3.5 shrink-0 ${c.spinning ? "animate-spin" : ""}`}
									aria-hidden="true"
								/>
								<span>{c.label}</span>
							</div>
							<div className="mt-1 text-2xl font-semibold tabular-nums">{c.count}</div>
						</div>
					);
				})}
		</div>
	);
}
